/**
 * Live www Register (Scout shot 02-register):
 * https://www.fuelcyclepro.com/register
 * Routed by Vite `App.jsx` → `src/components/Auth/Register.jsx` (no pages/Register).
 * Password managers can fire a late `change`/`input` after the rider has already
 * typed. Those writes must not replace a user-owned value. Empty fields still
 * accept the first autofill so a manager can fill unused boxes.
 */

export const REGISTER_FIELD_AUTOCOMPLETE = {
  firstName: 'given-name',
  lastName: 'family-name',
  email: 'email',
  password: 'new-password',
  confirmPassword: 'new-password',
  referralSource: 'off',
};

export const REGISTER_PROTECTED_FIELDS = [
  'firstName',
  'lastName',
  'password',
  'confirmPassword',
];

/** Browser email suggestions must commit. Empty DOM values do not clear a stored email. */
export function syncEmailFromDom(current, domValue) {
  if (typeof domValue !== 'string' || domValue.length === 0) return current;
  if (domValue === current) return current;
  return domValue;
}

const PROTECTED = new Set(REGISTER_PROTECTED_FIELDS);

export function isRegisterProtectedField(fieldName) {
  return PROTECTED.has(fieldName);
}

/** Keys that edit the value. Ignore Tab / modifiers / arrows so focus is not "typed". */
export function isRegisterUserEditKey(key) {
  if (typeof key !== 'string' || key.length === 0) return false;
  if (key.length === 1) return true;
  return key === 'Backspace' || key === 'Delete';
}

/**
 * `beforeinput` types that come from the rider. `insertReplacementText` is the
 * usual password-manager / autofill write and must not unlock a typed field.
 */
export function isRegisterUserBeforeInputType(inputType) {
  if (typeof inputType !== 'string' || inputType.length === 0) return false;
  if (inputType === 'insertReplacementText') return false;
  return inputType.startsWith('insert') || inputType.startsWith('delete');
}

export function createRegisterTypedFieldGuard() {
  const typed = new Set();
  const pendingUserEdit = new Set();

  return {
    markUserEdit(fieldName) {
      if (!isRegisterProtectedField(fieldName)) return;
      typed.add(fieldName);
      pendingUserEdit.add(fieldName);
    },
    shouldAcceptChange(fieldName) {
      if (!isRegisterProtectedField(fieldName)) return true;
      if (!typed.has(fieldName)) return true;
      if (pendingUserEdit.has(fieldName)) {
        pendingUserEdit.delete(fieldName);
        return true;
      }
      return false;
    },
    isTyped(fieldName) {
      return typed.has(fieldName);
    },
  };
}

/** Next form snapshot. Rejected autofill returns a new object so React re-applies the typed value. */
export function applyRegisterProtectedChange(prev, fieldName, nextValue, guard) {
  if (!guard || guard.shouldAcceptChange(fieldName)) {
    return { ...prev, [fieldName]: nextValue };
  }
  return { ...prev };
}
